import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const GA_MEASUREMENT_ID = "G-V1FSZ81GMF";
const META_PIXEL_ID = "931450819858392";

const CONSENT_KEY = "masonry_pro_cookie_consent";

type ConsentStatus = "accepted" | "opted_out" | null;

function getGPCSignal(): boolean {
  if (typeof navigator !== "undefined" && "globalPrivacyControl" in navigator) {
    return !!(navigator as any).globalPrivacyControl;
  }
  return false;
}

function loadGoogleAnalytics() {
  if (document.getElementById("ga-script")) return;

  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).gtag = function () {
    (window as any).dataLayer.push(arguments);
  };
  (window as any).gtag("js", new Date());
  (window as any).gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });
}

function disableGoogleAnalytics() {
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  // Remove existing GA cookies
  document.cookie.split(";").forEach((c) => {
    const name = c.trim().split("=")[0];
    if (name.startsWith("_ga") || name.startsWith("_gid")) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

function loadMetaPixel() {
  if ((window as any).fbq) return;

  // Standard Meta Pixel base code
  /* eslint-disable */
  (function (f: any, b, e, v, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.id = "meta-pixel-script";
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  (window as any).fbq("init", META_PIXEL_ID);
  (window as any).fbq("track", "PageView");
}

function disableMetaPixel() {
  // Prevent future loads
  const script = document.getElementById("meta-pixel-script");
  if (script) script.remove();

  // Remove pixel cookies (_fbp, _fbc)
  document.cookie.split(";").forEach((c) => {
    const name = c.trim().split("=")[0];
    if (name === "_fbp" || name === "_fbc") {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus;
    const gpc = getGPCSignal();

    if (gpc) {
      // GPC signal = automatic opt-out per NJDPA
      localStorage.setItem(CONSENT_KEY, "opted_out");
      disableGoogleAnalytics();
      disableMetaPixel();
      return;
    }

    if (stored === "accepted") {
      loadGoogleAnalytics();
      loadMetaPixel();
      return;
    }

    if (stored === "opted_out") {
      disableGoogleAnalytics();
      disableMetaPixel();
      return;
    }

    // No preference yet — show the banner
    setVisible(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    loadGoogleAnalytics();
    loadMetaPixel();
    setVisible(false);
  };

  const handleOptOut = () => {
    localStorage.setItem(CONSENT_KEY, "opted_out");
    disableGoogleAnalytics();
    disableMetaPixel();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-border bg-card/95 backdrop-blur-md px-4 py-4 md:py-5 animate-fade-up"
    >
      <div className="container mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground max-w-2xl">
          We use cookies to improve your experience and analyze site traffic. You may opt out of non-essential cookies at any time.{" "}
          <Link to="/privacy-policy" className="text-secondary underline underline-offset-2 hover:text-secondary/80">
            Read our Privacy Policy
          </Link>
        </p>
        <div className="flex gap-3 shrink-0">
          <Button variant="goldOutline" size="sm" onClick={handleOptOut}>
            Opt Out
          </Button>
          <Button variant="gold" size="sm" onClick={handleAccept}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
